import { appleSignIn, googleSignIn } from "@/api/auth";
import {
  generateCodeChallenge,
  generateCodeVerifier,
  generateNonce,
  generateState,
} from "@/lib/pkce";
import { useAuthStore } from "@/store/zustand/useAuthStore";
import {
  getAccountConflictPayload,
  getApiError,
  isApiClientError,
  type AccountConflictPayload,
} from "@membentry/shared-api";
import type { UserDetailDTO } from "@membentry/shared-types";

export const GOOGLE_SOCIAL_TRANSACTION_KEY =
  "membentry.social-auth.google.transaction.v1";
export const APPLE_SOCIAL_TRANSACTION_KEY =
  "membentry.social-auth.apple.transaction.v1";
export const GOOGLE_MFA_HANDOFF_KEY =
  "membentry.social-auth.google.mfa-handoff.v1";
export const GOOGLE_AUTH_FEEDBACK_KEY =
  "membentry.social-auth.google.feedback.v1";
export const SOCIAL_CONFLICT_LINK_HANDOFF_KEY =
  "membentry.social-auth.conflict-link-handoff.v1";

export const SOCIAL_AUTH_TRANSACTION_VERSION = 1 as const;
export const SOCIAL_AUTH_TRANSACTION_TTL_MS = 10 * 60 * 1000;

export type SocialProvider = "google" | "apple";
export type GoogleAuthMode = "login" | "link";
export type GoogleSocialAuthStage = "start" | "callback" | "exchange" | "session";
export type GoogleSocialAuthErrorCode =
  | "not_configured"
  | "transaction_missing"
  | "transaction_invalid"
  | "transaction_expired"
  | "state_mismatch"
  | "cancelled"
  | "provider_error"
  | "account_conflict"
  | "exchange_failed";

export class GoogleSocialAuthError extends Error {
  public readonly code: GoogleSocialAuthErrorCode;
  public readonly stage: GoogleSocialAuthStage;
  public readonly provider: SocialProvider;
  public readonly httpStatus?: number;
  public readonly conflictPayload?: AccountConflictPayload;
  public readonly cause?: unknown;

  constructor(input: {
    code: GoogleSocialAuthErrorCode;
    stage: GoogleSocialAuthStage;
    message: string;
    provider?: SocialProvider;
    httpStatus?: number;
    conflictPayload?: AccountConflictPayload;
    cause?: unknown;
  }) {
    super(input.message);
    this.name = "GoogleSocialAuthError";
    this.code = input.code;
    this.stage = input.stage;
    this.provider = input.provider ?? "google";
    this.httpStatus = input.httpStatus;
    this.conflictPayload = input.conflictPayload;
    this.cause = input.cause;
  }
}

export interface SocialTransaction {
  version: typeof SOCIAL_AUTH_TRANSACTION_VERSION;
  provider: SocialProvider;
  mode: GoogleAuthMode;
  state: string;
  locale: string;
  redirectUri: string;
  createdAt: number;
  codeVerifier?: string;
  nonce?: string;
}
export type GoogleSocialTransaction = SocialTransaction & {
  provider: "google";
  codeVerifier: string;
};
export type AppleSocialTransaction = SocialTransaction & {
  provider: "apple";
  nonce: string;
};

export type GoogleAuthOutcome =
  | { type: "signed-in"; user: UserDetailDTO | null | undefined }
  | { type: "mfa-required"; email: string }
  | { type: "mfa-setup-required"; user: UserDetailDTO | null | undefined };

type MfaHandoff = { version: 1; email: string; createdAt: number };
type AuthFeedback = { version: 1; type: "account-conflict"; createdAt: number };
export type SocialConflictLinkHandoff = AccountConflictPayload & {
  version: 1;
  createdAt: number;
};

const transactionKey = (provider: SocialProvider) =>
  provider === "google" ? GOOGLE_SOCIAL_TRANSACTION_KEY : APPLE_SOCIAL_TRANSACTION_KEY;
const getClientId = (provider: SocialProvider): string | undefined =>
  (provider === "google"
    ? process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID
    : process.env.NEXT_PUBLIC_APPLE_CLIENT_ID
  )?.trim() || undefined;

export const isGoogleSignInAvailable = () => Boolean(getClientId("google"));
export const isAppleSignInAvailable = () => Boolean(getClientId("apple"));

export const getSocialRedirectUri = (provider: SocialProvider, locale: string) => {
  const origin = typeof window === "undefined" ? "" : window.location.origin;
  return `${origin}/${locale}/auth/callback/${provider}`;
};
export const getGoogleRedirectUri = (locale: string) =>
  getSocialRedirectUri("google", locale);

const fail = (
  provider: SocialProvider,
  code: GoogleSocialAuthErrorCode,
  stage: GoogleSocialAuthStage,
  message: string,
  extra: { httpStatus?: number; cause?: unknown } = {},
): never => {
  throw new GoogleSocialAuthError({ provider, code, stage, message, ...extra });
};

const isTransaction = (
  value: unknown,
  provider: SocialProvider,
): value is SocialTransaction => {
  if (!value || typeof value !== "object") return false;
  const tx = value as Partial<SocialTransaction>;
  return (
    tx.version === SOCIAL_AUTH_TRANSACTION_VERSION &&
    tx.provider === provider &&
    (tx.mode === "login" || tx.mode === "link") &&
    typeof tx.state === "string" && tx.state.length > 0 &&
    typeof tx.locale === "string" && tx.locale.length > 0 &&
    typeof tx.redirectUri === "string" && tx.redirectUri.length > 0 &&
    typeof tx.createdAt === "number" && Number.isFinite(tx.createdAt) &&
    (provider !== "google" || (typeof tx.codeVerifier === "string" && tx.codeVerifier.length >= 43)) &&
    (provider !== "apple" || (typeof tx.nonce === "string" && tx.nonce.length > 0))
  );
};

const persistTransaction = (tx: SocialTransaction) =>
  sessionStorage.setItem(transactionKey(tx.provider), JSON.stringify(tx));

export const consumeSocialTransaction = (
  provider: SocialProvider,
  callbackLocale: string,
  callbackState: string | null,
  now = Date.now(),
): SocialTransaction => {
  const key = transactionKey(provider);
  const raw = sessionStorage.getItem(key);
  sessionStorage.removeItem(key);
  if (raw === null) return fail(provider, "transaction_missing", "callback", `${provider} transaction missing`);
  let parsed: unknown;
  try { parsed = JSON.parse(raw); } catch {
    fail(provider, "transaction_invalid", "callback", `${provider} transaction invalid`);
  }
  if (!isTransaction(parsed, provider) || parsed.locale !== callbackLocale ||
      parsed.redirectUri !== getSocialRedirectUri(provider, callbackLocale)) {
    return fail(provider, "transaction_invalid", "callback", `${provider} transaction mismatch`);
  }
  const transaction = parsed;
  const age = now - transaction.createdAt;
  if (age < 0 || age > SOCIAL_AUTH_TRANSACTION_TTL_MS)
    fail(provider, "transaction_expired", "callback", `${provider} transaction expired`);
  if (!callbackState || callbackState !== transaction.state)
    fail(provider, "state_mismatch", "callback", `${provider} state mismatch`);
  return transaction;
};

export const consumeGoogleTransaction = (
  locale: string, state: string | null, now = Date.now(),
): GoogleSocialTransaction =>
  consumeSocialTransaction("google", locale, state, now) as GoogleSocialTransaction;
export const consumeAppleTransaction = (
  locale: string, state: string | null, now = Date.now(),
): AppleSocialTransaction =>
  consumeSocialTransaction("apple", locale, state, now) as AppleSocialTransaction;

export const buildSocialAuthorizeUrl = async (
  provider: SocialProvider,
  options: { locale: string; mode?: GoogleAuthMode },
): Promise<string> => {
  const configuredClientId = getClientId(provider);
  if (!configuredClientId) return fail(provider, "not_configured", "start", `${provider} sign-in is not configured`);
  const clientId = configuredClientId;
  const redirectUri = getSocialRedirectUri(provider, options.locale);
  const state = generateState();
  const base = {
    version: SOCIAL_AUTH_TRANSACTION_VERSION,
    provider,
    mode: options.mode ?? "login",
    state,
    locale: options.locale,
    redirectUri,
    createdAt: Date.now(),
  } as const;

  if (provider === "google") {
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    persistTransaction({ ...base, codeVerifier });
    const params = new URLSearchParams({ client_id: clientId, redirect_uri: redirectUri,
      response_type: "code", scope: "openid email profile", state,
      code_challenge: codeChallenge, code_challenge_method: "S256" });
    return `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  }

  const nonce = generateNonce();
  persistTransaction({ ...base, nonce });
  const params = new URLSearchParams({ client_id: clientId, redirect_uri: redirectUri,
    response_type: "code", response_mode: "form_post", scope: "name email", state, nonce });
  return `https://appleid.apple.com/auth/authorize?${params}`;
};

export const buildGoogleAuthorizeUrl = (options: { locale: string; mode?: GoogleAuthMode }) =>
  buildSocialAuthorizeUrl("google", options);
export const buildAppleAuthorizeUrl = (options: { locale: string; mode?: GoogleAuthMode }) =>
  buildSocialAuthorizeUrl("apple", options);
export const startGoogleSignIn = async (options: { locale: string; mode?: GoogleAuthMode }) =>
  window.location.assign(await buildGoogleAuthorizeUrl(options));
export const startAppleSignIn = async (options: { locale: string; mode?: GoogleAuthMode }) =>
  window.location.assign(await buildAppleAuthorizeUrl(options));

const toSocialError = (provider: SocialProvider, error: unknown): GoogleSocialAuthError => {
  if (error instanceof GoogleSocialAuthError) return error;
  const apiError = getApiError(error);
  const httpStatus = isApiClientError(error) ? error.status : undefined;
  if (httpStatus === 409 || apiError?.code === "AccountConflict") {
    return new GoogleSocialAuthError({ provider, code: "account_conflict", stage: "exchange",
      message: "A password account already exists", httpStatus: httpStatus ?? 409,
      conflictPayload: getAccountConflictPayload(error) ?? undefined, cause: error });
  }
  return new GoogleSocialAuthError({ provider, code: "exchange_failed", stage: "exchange",
    message: `${provider} sign-in exchange failed`, httpStatus, cause: error });
};

export const completeSocialCallback = async (
  provider: SocialProvider,
  params: { code: string | null; state: string | null; error: string | null;
    locale: string; fullName?: string | null },
): Promise<GoogleAuthOutcome> => {
  if (params.error) {
    sessionStorage.removeItem(transactionKey(provider));
    fail(provider,
      params.error === "access_denied" || params.error === "user_cancelled" ? "cancelled" : "provider_error",
      "callback", `${provider} authorization returned an error`);
  }
  const tx = consumeSocialTransaction(provider, params.locale, params.state);
  if (!params.code) return fail(provider, "transaction_invalid", "callback", `${provider} code missing`);
  const code = params.code;
  if (tx.mode !== "login") return fail(provider, "transaction_invalid", "callback", "Link callbacks are disabled");
  try {
    const result = provider === "google"
      ? await googleSignIn({ code, codeVerifier: tx.codeVerifier!, redirectUri: tx.redirectUri })
      : await appleSignIn({ code, redirectUri: tx.redirectUri, nonce: tx.nonce,
          fullName: params.fullName?.trim() || undefined });
    if (result.success) {
      const { accessToken, refreshToken, user } = result.authResponse;
      const store = useAuthStore.getState();
      store.setTokens(accessToken, refreshToken);
      store.setUserData(user);
      return { type: "signed-in", user };
    }
    if ("requiresMfaSetup" in result) {
      const { accessToken, refreshToken, user } = result.authResponse;
      const store = useAuthStore.getState();
      store.setTokens(accessToken, refreshToken);
      store.setUserData(user);
      store.setRequiresMfaSetup(true);
      return { type: "mfa-setup-required", user };
    }
    return { type: "mfa-required", email: result.email };
  } catch (error) { throw toSocialError(provider, error); }
};

export const completeGoogleCallback = (params: Parameters<typeof completeSocialCallback>[1]) =>
  completeSocialCallback("google", params);
export const completeAppleCallback = (params: Parameters<typeof completeSocialCallback>[1]) =>
  completeSocialCallback("apple", params);

export const persistGoogleMfaHandoff = (email: string) =>
  sessionStorage.setItem(GOOGLE_MFA_HANDOFF_KEY, JSON.stringify({ version: 1, email, createdAt: Date.now() }));
export const consumeGoogleMfaHandoff = (now = Date.now()): string | null => {
  const raw = sessionStorage.getItem(GOOGLE_MFA_HANDOFF_KEY);
  sessionStorage.removeItem(GOOGLE_MFA_HANDOFF_KEY);
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<MfaHandoff>;
    const age = now - (value.createdAt ?? 0);
    return value.version === 1 && typeof value.email === "string" && value.email &&
      typeof value.createdAt === "number" && age >= 0 && age <= SOCIAL_AUTH_TRANSACTION_TTL_MS
      ? value.email : null;
  } catch { return null; }
};
export const persistSocialConflictLinkHandoff = (
  payload: AccountConflictPayload,
) => sessionStorage.setItem(
  SOCIAL_CONFLICT_LINK_HANDOFF_KEY,
  JSON.stringify({ ...payload, version: 1, createdAt: Date.now() }),
);

export const consumeSocialConflictLinkHandoff = (): SocialConflictLinkHandoff | null => {
  const raw = sessionStorage.getItem(SOCIAL_CONFLICT_LINK_HANDOFF_KEY);
  sessionStorage.removeItem(SOCIAL_CONFLICT_LINK_HANDOFF_KEY);
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<SocialConflictLinkHandoff>;
    if (
      value.version !== 1 ||
      (value.provider !== "google" && value.provider !== "apple") ||
      typeof value.email !== "string" || !value.email ||
      typeof value.linkTicket !== "string" || !value.linkTicket ||
      typeof value.expiresAt !== "string" || !value.expiresAt ||
      typeof value.createdAt !== "number"
    ) return null;
    return value as SocialConflictLinkHandoff;
  } catch { return null; }
};

export const persistGoogleAccountConflictFeedback = () =>
  sessionStorage.setItem(GOOGLE_AUTH_FEEDBACK_KEY, JSON.stringify({ version: 1, type: "account-conflict", createdAt: Date.now() }));
export const consumeGoogleAccountConflictFeedback = (now = Date.now()): boolean => {
  const raw = sessionStorage.getItem(GOOGLE_AUTH_FEEDBACK_KEY);
  sessionStorage.removeItem(GOOGLE_AUTH_FEEDBACK_KEY);
  if (!raw) return false;
  try {
    const value = JSON.parse(raw) as Partial<AuthFeedback>;
    const age = now - (value.createdAt ?? 0);
    return value.version === 1 && value.type === "account-conflict" &&
      typeof value.createdAt === "number" && age >= 0 && age <= SOCIAL_AUTH_TRANSACTION_TTL_MS;
  } catch { return false; }
};
